import React, { useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import fileTypeService, { FileType } from '../services/fileTypeService';
import { formatDate } from '../utils/dateUtils';

export const FileTypesPage: React.FC = () => {
  const { currentTenant } = useTenant();
  const [fileTypes, setFileTypes] = useState<FileType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [owner, setOwner] = useState<'PATIENT' | 'MEETING'>('PATIENT');

  const loadFileTypes = React.useCallback(async () => {
    if (!currentTenant) return;
    try {
      setIsLoading(true);
      const data = await fileTypeService.getFileTypes(currentTenant.id);
      setFileTypes(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load file types:', err);
      setError('Failed to load file types');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant]);

  useEffect(() => {
    loadFileTypes();
  }, [loadFileTypes]);

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setName('');
    setOwner('PATIENT');
  };

  const handleEdit = (fileType: FileType) => {
    setEditingId(fileType.id);
    setIsAdding(false);
    setName(fileType.name);
    setOwner(fileType.owner);
  };

  const handleSave = async () => {
    if (!currentTenant || !name.trim()) return;

    try {
      if (isAdding) {
        await fileTypeService.createFileType(currentTenant.id, { name, owner });
      } else if (editingId) {
        await fileTypeService.updateFileType(editingId, { name, owner });
      }
      await loadFileTypes();
      handleCancel();
    } catch (err) {
      console.error('Failed to save file type:', err);
      setError('Failed to save file type');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this file type?')) return;

    try {
      await fileTypeService.deleteFileType(id);
      await loadFileTypes();
    } catch (err) {
      console.error('Failed to delete file type:', err);
      setError('Failed to delete file type');
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setName('');
    setOwner('PATIENT');
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">File Types</h1>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
          >
            + Add File Type
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {(isAdding || editingId) && (
          <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
            <h3 className="text-lg font-semibold mb-4">
              {isAdding ? 'Add New File Type' : 'Edit File Type'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter file type name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Owner
                </label>
                <select
                  value={owner}
                  onChange={(e) => setOwner(e.target.value as 'PATIENT' | 'MEETING')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="PATIENT">Patient</option>
                  <option value="MEETING">Meeting</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* File Types Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fileTypes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    No file types found. Click "Add File Type" to create one.
                  </td>
                </tr>
              ) : (
                fileTypes.map((fileType) => (
                  <tr key={fileType.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {fileType.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        fileType.owner === 'PATIENT'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {fileType.owner}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(fileType.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(fileType)}
                        className="text-teal-600 hover:text-teal-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(fileType.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
